import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { logger } from "../../lib/logger";
import { useData } from "../useData";
import { useRBAC } from "../useRBAC";
import { toOrgStartISO, toOrgEndISO, toOrgDateKey } from "../../lib/timezone";
import { CACHE_VERSION } from "../../lib/cacheVersion";
import { GLOBAL_DATE_RANGE_KEY, DATE_RANGE_CHANGE_EVENT } from "../useGlobalDateRange";

// =============================================================================
// useDashboardPrefetch - BACKGROUND DASHBOARD DATA PREFETCHER
// =============================================================================
// PURPOSE: Silently pre-loads dashboard KPIs + daily chart data on app mount,
// so the Dashboard page renders instantly when the user navigates to it.
//
// ROOT CAUSE: SQL executes in <1ms, but network round-trips from PH (~200-500ms
// per call) make 12+ parallel Supabase calls take ~2-3s total. By prefetching
// from AppLayout, the cache is warm before the user clicks Dashboard.
//
// ARCHITECTURE:
// - Runs from AppLayout (alongside useCalendarPrefetch, useDMAnalyticsPrefetch)
// - Fire-and-forget: no loading states, no error UI
// - Skips if caches are already warm (no redundant fetches)
// - Uses the SAME cache keys + prefix as useOperationsAnalytics so hooks get
//   instant cache hits on mount
// - Reads the persisted date range from localStorage (same as useGlobalDateRange)
// - Re-fires on DATE_RANGE_CHANGE_EVENT (debounced 300ms, deduped by ISO key)
// =============================================================================

const OPS_KPI_CACHE_PREFIX = `altara_${CACHE_VERSION}_ops_kpi_`;
const OPS_DM_CACHE_PREFIX = `altara_${CACHE_VERSION}_ops_dm_`;

// MUST mirror useOperationsAnalytics' TTLs (its cache readers aren't exported).
// 10 min live / 24h historical — if these drift, the skip-guard either skips a
// re-warm the consumer will reject (cold dashboard) or re-warms entries the
// consumer still serves (wasted RPCs).
const OPS_CACHE_TTL = 10 * 60 * 1000;
const OPS_HISTORICAL_TTL = 24 * 60 * 60 * 1000;

function hasCacheEntry(prefix: string, key: string, orgId: string): boolean {
  try {
    const raw = localStorage.getItem(prefix + key);
    if (!raw) return false;
    const entry = JSON.parse(raw);
    if (entry.orgId !== orgId) return false;
    // P0.5-CACHE (Jun 13 2026): mirror the consumer's TTL instead of "exists =
    // warm". The old org-only check skipped the re-warm even when the
    // consumer's own TTL had already expired the entry → user landed on a
    // cold dashboard after a "warm" prefetch. TTL tiering copied verbatim
    // from useOperationsAnalytics.getOpsKpiCache/getOpsDmCache.
    const dateMatch = key.match(/(\d{4}-\d{2}-\d{2})T/g);
    let ttl = OPS_CACHE_TTL;
    if (dateMatch && dateMatch.length >= 2) {
      const endDateStr = dateMatch[1].replace("T", "");
      const endDate = new Date(endDateStr + "T23:59:59");
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (endDate < today) ttl = OPS_HISTORICAL_TTL;
    }
    return Date.now() - entry.timestamp <= ttl;
  } catch {
    return false;
  }
}

function setCacheEntry<T>(prefix: string, key: string, data: T, orgId: string): void {
  try {
    localStorage.setItem(prefix + key, JSON.stringify({ data, timestamp: Date.now(), orgId }));
  } catch {
    // localStorage full — ignore
  }
}

/** Load the persisted date range (same source as useGlobalDateRange) */
function loadDateRange(): { start: Date; end: Date } {
  try {
    const stored = localStorage.getItem(GLOBAL_DATE_RANGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.start && parsed.end) {
        return { start: new Date(parsed.start), end: new Date(parsed.end) };
      }
    }
  } catch {
    // Ignore
  }
  // Default: last 30 days (matches useGlobalDateRange default)
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 29);
  return { start, end };
}

export function useDashboardPrefetch(): void {
  const { currentOrganization } = useData();
  const { role } = useRBAC();
  const location = useLocation();
  const orgId = currentOrganization?.id ?? null;
  const orgTz = currentOrganization?.timezone || "UTC";
  const lastOrgRef = useRef<string | null>(null);
  const lastWarmedRangeRef = useRef<string | null>(null);

  // ---------------------------------------------------------------------------
  // Core prefetch logic — extracted so BOTH the org-switch effect and the
  // date-change event listener can call it.
  // ---------------------------------------------------------------------------
  const runPrefetch = async (
    dateRange: { start: Date; end: Date },
    db: NonNullable<typeof supabase>,
    resolvedOrgId: string,
    resolvedOrgTz: string
  ) => {
    try {
      const startISO = toOrgStartISO(dateRange.start, resolvedOrgTz);
      const endISO = toOrgEndISO(dateRange.end, resolvedOrgTz);

      // Previous period for % changes
      const duration = dateRange.end.getTime() - dateRange.start.getTime();
      const daysInRange = Math.round(duration / (24 * 60 * 60 * 1000));
      const prevEndDate = new Date(dateRange.start);
      prevEndDate.setDate(prevEndDate.getDate() - 1);
      const prevStartDate = new Date(prevEndDate);
      prevStartDate.setDate(prevStartDate.getDate() - daysInRange);
      const prevStartISO = toOrgStartISO(prevStartDate, resolvedOrgTz);
      const prevEndISO = toOrgEndISO(prevEndDate, resolvedOrgTz);

      // Cache keys — must match useOperationsAnalytics exactly
      const currKey = `curr_${resolvedOrgId}_${startISO}_${endISO}`;
      const prevKey = `prev_${resolvedOrgId}_${prevStartISO}_${prevEndISO}`;
      const dmKey = `v2_${resolvedOrgId}_${startISO}_${endISO}`;

      // Skip if all caches are warm
      const hasCurr = hasCacheEntry(OPS_KPI_CACHE_PREFIX, currKey, resolvedOrgId);
      const hasPrev = hasCacheEntry(OPS_KPI_CACHE_PREFIX, prevKey, resolvedOrgId);
      const hasDm = hasCacheEntry(OPS_DM_CACHE_PREFIX, dmKey, resolvedOrgId);

      if (hasCurr && hasPrev && hasDm) {
        logger.debug("Dashboard prefetch: all caches warm, skipping");
        return;
      }

      // Fire all uncached fetches in parallel
      // Wrap in async IIFEs because Supabase returns PromiseLike, not full Promise
      const promises: Promise<void>[] = [];

      if (!hasCurr) {
        promises.push(
          (async () => {
            const { data, error } = await db.rpc("get_operations_kpis", {
              p_organization_id: resolvedOrgId,
              p_start_date: startISO,
              p_end_date: endISO,
            });
            if (!error && data) {
              setCacheEntry(OPS_KPI_CACHE_PREFIX, currKey, data, resolvedOrgId);
            }
          })()
        );
      }

      if (!hasPrev) {
        promises.push(
          (async () => {
            const { data, error } = await db.rpc("get_operations_kpis", {
              p_organization_id: resolvedOrgId,
              p_start_date: prevStartISO,
              p_end_date: prevEndISO,
            });
            if (!error && data) {
              setCacheEntry(OPS_KPI_CACHE_PREFIX, prevKey, data, resolvedOrgId);
            }
          })()
        );
      }

      // Daily chart data — warms Supabase connection pool
      // (daily_org_metrics uses React state, not localStorage, in useOperationsAnalytics)
      promises.push(
        (async () => {
          await db
            .from("daily_org_metrics")
            .select(
              "date, cash_collected, revenue_closed, high_ticket_cash, low_ticket_cash, installments_collected"
            )
            .eq("organization_id", resolvedOrgId)
            .gte("date", toOrgDateKey(dateRange.start))
            .lte("date", toOrgDateKey(dateRange.end))
            .order("date");
        })()
      );

      if (!hasDm) {
        promises.push(
          (async () => {
            const { data, error } = await db.rpc("get_dm_analytics_fast", {
              p_organization_id: resolvedOrgId,
              p_start_date: startISO,
              p_end_date: endISO,
            });
            if (!error && data && data.length > 0) {
              const r = data[0] as Record<string, unknown>;
              const dmStatsData = {
                // R4/R6: canonical lead-replied count — MUST match useOperationsAnalytics's
                // live mapping or the prefetch cache serves a 2.8x-raw number until refetch.
                newDMChats: (r.total_new_dms_lead_replied as number) || 0,
                conversations: (r.total_real_convos as number) || 0,
                totalConversations:
                  ((r.total_new_dms as number) || 0) + ((r.total_outbound_attempts as number) || 0),
                deepConversations: (r.total_deep_convos as number) ?? 0,
              };
              setCacheEntry(OPS_DM_CACHE_PREFIX, dmKey, dmStatsData, resolvedOrgId);
            }
          })()
        );
      }

      await Promise.all(promises);
      logger.info("Dashboard prefetch complete: KPIs + DM stats cached");
    } catch (err) {
      // Silent failure — prefetch is best-effort
      logger.debug("Dashboard prefetch failed (non-critical):", err);
    }
  };

  // ---------------------------------------------------------------------------
  // Org-switch effect (original behavior — unchanged)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // Skip when already on the dashboard — the dashboard's own hooks handle data loading.
    // Prefetch is only useful when user is on OTHER pages (warming cache before they navigate).
    // Without this guard, prefetch fires 4 duplicate requests simultaneously with the dashboard's
    // own 12+ hooks, doubling network load at the worst possible moment.
    const onDashboard =
      location.pathname === "/admin/dashboard" || location.pathname === "/admin/dashboard/";
    if (onDashboard) return;

    // client sees the client portal, not the admin dashboard — skip prefetch
    if (!orgId || !supabase || orgId === lastOrgRef.current || role === "client") return;
    lastOrgRef.current = orgId;

    const db = supabase; // Capture for TypeScript narrowing in async closure
    const dateRange = loadDateRange();

    // Track warmed range so the date-change listener can dedupe
    const key = `${dateRange.start.toISOString()}_${dateRange.end.toISOString()}`;
    lastWarmedRangeRef.current = key;

    runPrefetch(dateRange, db, orgId, orgTz);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, orgTz, location.pathname]);

  // ---------------------------------------------------------------------------
  // Date-change listener — re-warms cache when the user changes the date picker.
  // Debounced 300ms because preset clicks can fire 2-3 events back-to-back.
  // Deduped by ISO range key so we don't double-fire if the range didn't change.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!orgId || !supabase || role === "client") return;

    const db = supabase;
    const resolvedOrgId = orgId;
    const resolvedOrgTz = orgTz;

    const fireWarm = () => {
      const onDashboard =
        location.pathname === "/admin/dashboard" || location.pathname === "/admin/dashboard/";
      if (onDashboard) return; // dashboard handles its own loading

      const fresh = loadDateRange();
      const rangeKey = `${fresh.start.toISOString()}_${fresh.end.toISOString()}`;
      if (lastWarmedRangeRef.current === rangeKey) return; // already warm for this range
      lastWarmedRangeRef.current = rangeKey;

      runPrefetch(fresh, db, resolvedOrgId, resolvedOrgTz);
    };

    let timer: ReturnType<typeof setTimeout> | null = null;
    const debounced = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(fireWarm, 300);
    };

    window.addEventListener(DATE_RANGE_CHANGE_EVENT, debounced);
    return () => {
      window.removeEventListener(DATE_RANGE_CHANGE_EVENT, debounced);
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, orgTz, location.pathname]);
}
